<!-- app-subdomain-creators\src\Components\ModernColorPicker.vue -->

<template>
  <div class="space-y-3 select-none shadow-md rounded-xl p-5 bg-white border border-zinc-100">
    <div
      ref="sv"
      class="relative w-full h-40 rounded-lg cursor-pointer"
      :style="{ background: `hsl(${hue}, 100%, 50%)` }"
      @mousedown="startSV"
    >
      <div class="absolute inset-0 rounded-lg bg-gradient-to-r from-white" />
      <div class="absolute inset-0 rounded-lg bg-gradient-to-t from-black" />
      <div
        class="absolute w-6 h-6 rounded-full border-2 border-white shadow"
        :style="{
          left: `${saturation}%`,
          top: `${100 - value}%`,
          transform: 'translate(-50%, -50%)',
          backgroundColor: currentColor,
        }"
      />
    </div>

    <div
      ref="hueEl"
      class="relative h-4 rounded-full cursor-pointer"
      :style="{
        background: `linear-gradient(to right,
          #ff0000,
          #ff7f00,
          #ffff00,
          #80ff00,
          #00ff00,
          #00ff80,
          #00ffff,
          #0080ff,
          #0000ff,
          #4b00ff,
          #8f00ff,
          #ff00ff,
          #ff0080,
          #ff0000
        )`,
      }"
      @mousedown="startHue"
    >
      <div
        class="absolute w-6 h-6 rounded-full border-2 border-white shadow"
        :style="{
          left: `${(hue / 360) * 100}%`,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: `hsl(${hue}, 100%, 50%)`,
        }"
      />
    </div>

    <div
      v-if="enableOpacity"
      class="space-y-1"
    >
      <div class="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-zinc-500">
        <span>Opacity</span>
        <span>{{ alpha }}%</span>
      </div>
      <input
        v-model.number="alpha"
        type="range"
        min="0"
        max="100"
        step="1"
        class="w-full accent-zinc-700"
      >
    </div>

    <div class="flex items-center gap-3 w-full">
      <div class="flex gap-2 border border-zinc-200 rounded-lg p-2 items-center flex-1">
        <div
          class="relative w-7 h-7 rounded-full border border-zinc-200 overflow-hidden"
          :style="checkerStyle"
        >
          <div
            class="absolute inset-0"
            :style="{ backgroundColor: currentColor }"
          />
        </div>

        <input
          v-model="hex"
          type="text"
          class="border-none px-2 py-1 rounded w-28 text-base font-semibold"
          :disabled="allowTransparent && isTransparent"
        >
      </div>

      <button
        v-if="allowTransparent"
        type="button"
        class="px-2.5 py-2 rounded-lg border text-xs font-medium transition"
        :class="isTransparent ? 'border-zinc-700 bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-700 hover:bg-zinc-100'"
        @click="setTransparent"
      >
        Transparent
      </button>

      <button
        type="button"
        class="p-3 hover:bg-zinc-100 border border-zinc-200 rounded-lg"
        @click="pickColor"
      >
        <EyeDropperIcon class="text-zinc-800 h-6" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { EyeDropperIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  modelValue: {
    type: String,
    default: '#FF0000',
  },
  allowTransparent: {
    type: Boolean,
    default: false,
  },
  enableOpacity: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const hue = ref(0)
const saturation = ref(100)
const value = ref(100)
const alpha = ref(100)
const isTransparent = ref(false)
const hex = ref('#FF0000')

const sv = ref(null)
const hueEl = ref(null)

const checkerStyle = {
  backgroundColor: '#ffffff',
  backgroundImage: 'linear-gradient(45deg, #e4e4e7 25%, transparent 25%), linear-gradient(-45deg, #e4e4e7 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e4e4e7 75%), linear-gradient(-45deg, transparent 75%, #e4e4e7 75%)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
}

const currentRgb = computed(() => hsvToRgb(hue.value, saturation.value, value.value))
const currentHex = computed(() => rgbToHex(currentRgb.value.r, currentRgb.value.g, currentRgb.value.b))
const currentColor = computed(() => {
  if (props.allowTransparent && isTransparent.value) return 'transparent'
  if (props.enableOpacity && alpha.value < 100) {
    const { r, g, b } = currentRgb.value
    return `rgba(${r}, ${g}, ${b}, ${roundAlpha(alpha.value)})`
  }
  return currentHex.value
})

const outputColor = computed(() => {
  if (props.allowTransparent && isTransparent.value) return 'transparent'
  if (props.enableOpacity && alpha.value < 100) {
    const { r, g, b } = currentRgb.value
    return `rgba(${r}, ${g}, ${b}, ${roundAlpha(alpha.value)})`
  }
  return currentHex.value
})

watch(currentHex, (next) => {
  if (!(props.allowTransparent && isTransparent.value)) {
    hex.value = next
  }
})

watch(outputColor, (next) => {
  emit('update:modelValue', next)
})

watch(hex, (next) => {
  const normalized = normalizeHex(next)
  if (!normalized) return
  isTransparent.value = false
  const { h, s, v } = rgbToHSV(
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
  )
  hue.value = h
  saturation.value = s
  value.value = v
})

watch(
  () => props.modelValue,
  (next) => {
    syncFromModel(next)
  },
  { immediate: true }
)

function clamp(value, min, max) {
  const n = Number(value)
  if (!Number.isFinite(n)) return min
  return Math.min(max, Math.max(min, n))
}

function normalizeHex(input) {
  const raw = String(input || '').trim()
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toUpperCase()
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toUpperCase()}`
  return ''
}

function roundAlpha(percent) {
  return Number((clamp(percent, 0, 100) / 100).toFixed(2))
}

function parseColorString(input) {
  const raw = String(input || '').trim()
  if (!raw) return null
  if (raw.toLowerCase() === 'transparent') {
    return { r: 0, g: 0, b: 0, a: 0, transparent: true }
  }
  const hexColor = normalizeHex(raw)
  if (hexColor) {
    return {
      r: parseInt(hexColor.slice(1, 3), 16),
      g: parseInt(hexColor.slice(3, 5), 16),
      b: parseInt(hexColor.slice(5, 7), 16),
      a: 1,
      transparent: false,
    }
  }
  const rgbMatch = raw.match(/^rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)$/i)
  if (rgbMatch) {
    return {
      r: clamp(rgbMatch[1], 0, 255),
      g: clamp(rgbMatch[2], 0, 255),
      b: clamp(rgbMatch[3], 0, 255),
      a: 1,
      transparent: false,
    }
  }
  const rgbaMatch = raw.match(/^rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([01](?:\.\d+)?)\s*\)$/i)
  if (rgbaMatch) {
    return {
      r: clamp(rgbaMatch[1], 0, 255),
      g: clamp(rgbaMatch[2], 0, 255),
      b: clamp(rgbaMatch[3], 0, 255),
      a: clamp(rgbaMatch[4], 0, 1),
      transparent: false,
    }
  }
  return null
}

function syncFromModel(modelValue) {
  const parsed = parseColorString(modelValue)
  if (!parsed) return

  if (props.allowTransparent && parsed.transparent) {
    isTransparent.value = true
    alpha.value = 0
    return
  }

  isTransparent.value = false
  const { h, s, v } = rgbToHSV(parsed.r, parsed.g, parsed.b)
  hue.value = h
  saturation.value = s
  value.value = v
  alpha.value = Math.round(clamp(parsed.a, 0, 1) * 100)
  hex.value = rgbToHex(parsed.r, parsed.g, parsed.b)
}

function rgbToHSV(r, g, b) {
  const nr = clamp(r, 0, 255) / 255
  const ng = clamp(g, 0, 255) / 255
  const nb = clamp(b, 0, 255) / 255

  const max = Math.max(nr, ng, nb)
  const min = Math.min(nr, ng, nb)
  const d = max - min

  let h = 0
  if (d !== 0) {
    if (max === nr) h = ((ng - nb) / d + (ng < nb ? 6 : 0)) * 60
    else if (max === ng) h = ((nb - nr) / d + 2) * 60
    else h = ((nr - ng) / d + 4) * 60
  }

  const s = max === 0 ? 0 : d / max
  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    v: Math.round(max * 100),
  }
}

function hsvToRgb(h, s, v) {
  const hh = clamp(h, 0, 360)
  const ss = clamp(s, 0, 100) / 100
  const vv = clamp(v, 0, 100) / 100

  const c = vv * ss
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1))
  const m = vv - c

  let r = 0
  let g = 0
  let b = 0

  if (hh < 60) [r, g, b] = [c, x, 0]
  else if (hh < 120) [r, g, b] = [x, c, 0]
  else if (hh < 180) [r, g, b] = [0, c, x]
  else if (hh < 240) [r, g, b] = [0, x, c]
  else if (hh < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  }
}

function rgbToHex(r, g, b) {
  return (
    '#' +
    [r, g, b]
      .map((n) => clamp(n, 0, 255).toString(16).padStart(2, '0'))
      .join('')
  ).toUpperCase()
}

async function pickColor() {
  if (!window.EyeDropper) {
    alert('Eyedropper is not supported in this browser.')
    return
  }

  try {
    const eye = new window.EyeDropper()
    const result = await eye.open()
    isTransparent.value = false
    if (props.enableOpacity && alpha.value === 0) alpha.value = 100
    hex.value = normalizeHex(result?.sRGBHex) || '#FF0000'
  } catch (_err) {
    // User canceled eyedropper.
  }
}

function setTransparent() {
  if (!props.allowTransparent) return
  isTransparent.value = true
  if (props.enableOpacity) alpha.value = 0
}

function startSV(event) {
  const rect = sv.value?.getBoundingClientRect?.()
  if (!rect) return
  moveSV(event, rect)

  const move = (ev) => moveSV(ev, rect)
  const stop = () => {
    window.removeEventListener('mousemove', move)
    window.removeEventListener('mouseup', stop)
  }

  window.addEventListener('mousemove', move)
  window.addEventListener('mouseup', stop)
}

function moveSV(event, rect) {
  const x = clamp(event.clientX - rect.left, 0, rect.width)
  const y = clamp(event.clientY - rect.top, 0, rect.height)
  if (props.allowTransparent && isTransparent.value) isTransparent.value = false
  saturation.value = Math.round((x / rect.width) * 100)
  value.value = Math.round(100 - (y / rect.height) * 100)
}

function startHue(event) {
  const rect = hueEl.value?.getBoundingClientRect?.()
  if (!rect) return
  moveHue(event, rect)

  const move = (ev) => moveHue(ev, rect)
  const stop = () => {
    window.removeEventListener('mousemove', move)
    window.removeEventListener('mouseup', stop)
  }

  window.addEventListener('mousemove', move)
  window.addEventListener('mouseup', stop)
}

function moveHue(event, rect) {
  const x = clamp(event.clientX - rect.left, 0, rect.width)
  if (props.allowTransparent && isTransparent.value) isTransparent.value = false
  hue.value = Math.round((x / rect.width) * 360)
}
</script>
